// ─── DriverQuickEditDialog — edição rápida de um motorista ──────────────────
// Reutilizável (ex.: vista de execução das Operações). Edita contacto/
// identificação (telemóvel, cartão de tacógrafo, RFID, carta, email, categoria)
// de um motorista LOCAL, por id exato → PUT /planner-api/drivers/:id.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  TextField, Button, IconButton, MenuItem, CircularProgress, InputAdornment,
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import PhoneIcon from '@material-ui/icons/Phone';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ContactlessIcon from '@material-ui/icons/Contactless';
import CheckIcon from '@material-ui/icons/Check';

function DriverQuickEditDialog({ open, onClose, driver, token, lang = 'pt', onSaved }) {
  const pt = lang === 'pt';
  const [form, setForm] = useState({});
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch('/planner-api/driver-categories', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) { const d = await r.json(); setCategories(Array.isArray(d) ? d : []); }
    } catch { /* silencioso */ }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    fetchCategories();
    setError(null);
    const prefill = (d) => setForm({
      name: d?.name || '',
      phone: d?.phone || '',
      tachoCard: d?.tachoCard || '',
      rfid: d?.rfid || '',
      licenseClass: d?.licenseClass || '',
      email: d?.email || '',
      category: d?.category || '',
    });
    // Prefill imediato com o que recebemos, depois ATUALIZA com o registo
    // completo e atual vindo da BD (evita gravar campos vazios por prefill
    // incompleto — ex.: quando o motorista vem de um cache parcial).
    prefill(driver);
    if (driver?.id && token) {
      fetch('/planner-api/drivers', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(list => {
          const full = (Array.isArray(list) ? list : []).find(d => String(d.id) === String(driver.id));
          if (full) prefill(full);
        })
        .catch(() => {});
    }
  }, [open, driver, token, fetchCategories]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const catNames = categories.map((c) => c.name);

  async function save() {
    if (!driver?.id) { setError(pt ? 'Motorista sem registo local — não é possível editar aqui.' : 'No local record.'); return; }
    // O nome só é editável em motoristas LOCAIS (sem externalId). Nos do
    // EcooGPS não enviamos o nome (o backend também o ignora).
    const canEditName = !driver?.externalId;
    const newName = (form.name || '').trim();
    if (canEditName && !newName) { setError(pt ? 'O nome é obrigatório.' : 'Name is required.'); return; }
    setSaving(true); setError(null);
    try {
      const r = await fetch(`/planner-api/drivers/${driver.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(canEditName ? { name: newName } : {}),
          phone: form.phone, tachoCard: form.tachoCard, rfid: form.rfid,
          licenseClass: form.licenseClass, email: form.email, category: form.category,
        }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      const updated = await r.json();
      if (onSaved) onSaved(updated);
      onClose();
    } catch (e) {
      setError(e.message || (pt ? 'Erro ao guardar.' : 'Save error.'));
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={!!open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 16 } }}>
      <DialogTitle style={{ paddingBottom: 4 }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Box style={{ width: 34, height: 34, borderRadius: '50%', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PersonIcon style={{ fontSize: 18, color: '#4f46e5' }} />
          </Box>
          <Box style={{ minWidth: 0 }}>
            <Typography style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{driver?.name || (pt ? 'Motorista' : 'Driver')}</Typography>
            <Typography style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
              {driver?.externalId ? `EcooGPS #${driver.externalId}` : (pt ? 'Motorista local' : 'Local driver')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 12 }}>
        {error && <Box style={{ padding: '9px 12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, fontSize: '0.76rem' }}>{error}</Box>}
        <TextField size="small" variant="outlined" fullWidth label={pt ? 'Nome' : 'Name'}
          value={form.name || ''} onChange={setField('name')}
          disabled={!!driver?.externalId}
          helperText={driver?.externalId
            ? (pt ? 'Nome gerido no EcooGPS — só se altera lá.' : 'Managed in EcooGPS.')
            : (pt ? 'Motorista do planner — nome editável.' : 'Planner driver — editable name.')}
          InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
        <TextField size="small" variant="outlined" fullWidth label={pt ? 'Telemóvel' : 'Phone'}
          value={form.phone || ''} onChange={setField('phone')}
          InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
        <TextField size="small" variant="outlined" fullWidth label={pt ? 'Cartão de tacógrafo' : 'Tachograph card'}
          value={form.tachoCard || ''} onChange={setField('tachoCard')}
          InputProps={{ startAdornment: <InputAdornment position="start"><CreditCardIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
        <TextField size="small" variant="outlined" fullWidth label="RFID"
          value={form.rfid || ''} onChange={setField('rfid')}
          InputProps={{ startAdornment: <InputAdornment position="start"><ContactlessIcon style={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }} />
        <TextField size="small" variant="outlined" fullWidth label={pt ? 'Carta de condução' : 'License class'}
          value={form.licenseClass || ''} onChange={setField('licenseClass')} />
        <TextField size="small" variant="outlined" fullWidth label="Email" type="email"
          value={form.email || ''} onChange={setField('email')} />
        <TextField select size="small" variant="outlined" fullWidth label={pt ? 'Categoria' : 'Category'}
          value={form.category || ''} onChange={setField('category')}>
          <MenuItem value=""><em>{pt ? '— sem categoria' : '— none'}</em></MenuItem>
          {categories.map((c) => (
            <MenuItem key={c.id} value={c.name}>
              <Box component="span" style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: c.color, marginRight: 8, verticalAlign: 'middle' }} />
              {c.name}
            </MenuItem>
          ))}
          {form.category && !catNames.includes(form.category) && <MenuItem value={form.category}>{form.category}</MenuItem>}
        </TextField>
      </DialogContent>
      <DialogActions style={{ padding: '12px 20px' }}>
        <Button onClick={onClose} disabled={saving} style={{ textTransform: 'none', color: '#64748b' }}>{pt ? 'Cancelar' : 'Cancel'}</Button>
        <Button onClick={save} disabled={saving} variant="contained" disableElevation
          style={{ textTransform: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700 }}
          startIcon={saving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 18 }} />}>
          {pt ? 'Guardar' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DriverQuickEditDialog;
